import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API
});

// Request interceptor to always get fresh token from localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("cia_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      localStorage.removeItem("cia_token");
      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Company API
export const companyApi = {
  getMyCompany: () => api.get(`/companies/me`),
  createCompany: (data) => api.post(`/companies`, data),
  updateCompany: (data) => api.put(`/companies/me`, data),
};

// Clients API
export const clientsApi = {
  list: () => api.get(`/clients`),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post(`/clients`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  validateSat: (id) => api.post(`/clients/${id}/validate-sat`),
  import: (data) => api.post(`/clients/import`, data),
  getStatement: (id) => api.get(`/clients/${id}/statement`),
};

// Invoices API
export const invoicesApi = {
  list: (params) => api.get(`/invoices`, { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post(`/invoices`, data),
  issue: (id) => api.post(`/invoices/${id}/issue`),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
};

// Credit Notes API
export const creditNotesApi = {
  list: () => api.get(`/credit-notes`),
  create: (data) => api.post(`/credit-notes`, data),
  issue: (id) => api.post(`/credit-notes/${id}/issue`),
};

// Payment Complements API
export const paymentComplementsApi = {
  list: () => api.get(`/payment-complements`),
  create: (data) => api.post(`/payment-complements`, data),
  issue: (id) => api.post(`/payment-complements/${id}/issue`),
};

// Reports API
export const reportsApi = {
  generate: (data) => api.post(`/reports/generate`, data),
};

// Chat API
export const chatApi = {
  sendMessage: (data) => api.post(`/chat/message`, data),
  getHistory: () => api.get(`/chat/history`),
};

// Subscription API
export const subscriptionApi = {
  getPlans: () => api.get(`/subscription-plans`),
  createCheckout: (data) => api.post(`/payments/checkout`, data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Admin API
export const adminApi = {
  getStats: () => api.get(`/admin/stats`),
  getCompanies: () => api.get(`/admin/companies`),
  updateSubscription: (companyId, data) => api.put(`/admin/companies/${companyId}/subscription`, null, { params: data }),
};

// Quotes API (Cotizaciones)
export const quotesApi = {
  list: (params) => api.get(`/quotes`, { params }),
  get: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post(`/quotes`, data),
  update: (id, data) => api.patch(`/quotes/${id}`, data),
  send: (id) => api.post(`/quotes/${id}/send`),
  convert: (id, data) => api.post(`/quotes/${id}/convert`, data),
  accept: (id) => api.post(`/quotes/${id}/accept`),
  reject: (id) => api.post(`/quotes/${id}/reject`),
  delete: (id) => api.delete(`/quotes/${id}`),
};

// Carta Porte API
export const cartaPorteApi = {
  getCatalogs: () => api.get(`/carta-porte/catalogs`),
  list: (params) => api.get(`/carta-porte`, { params }),
  get: (id) => api.get(`/carta-porte/${id}`),
  create: (data) => api.post(`/carta-porte`, data),
  issue: (id) => api.post(`/carta-porte/${id}/issue`),
  cancel: (id, motivo) => api.post(`/carta-porte/${id}/cancel`, null, { params: { motivo } }),
  delete: (id) => api.delete(`/carta-porte/${id}`),
};

export default {
  companyApi,
  clientsApi,
  invoicesApi,
  creditNotesApi,
  paymentComplementsApi,
  reportsApi,
  chatApi,
  subscriptionApi,
  adminApi,
  quotesApi,
  cartaPorteApi,
};
